/**
 * Helper générique : un store global indexé par `conversation_id`.
 *
 * Pour chaque conversation, on garde un state `reactive<T>` dans une Map.
 * Le `currentState` est un `computed` qui expose le state de la conv
 * courante (ou un état vide constant si aucune conv n'est sélectionnée).
 *
 * Pourquoi : `gatheringArtifactStore` et `persistedArtifactsStore` ont
 * exactement le même besoin — un état reactif partagé, cloisonné par
 * conversation, avec rehydration et reset au switch d'identité.
 *
 * Pattern aligné sur `artifactCorrelationStore.ts` (singleton module),
 * étendu avec une dimension conversation pour éviter la pollution croisée.
 */

import { computed, reactive, ref, type ComputedRef } from 'vue'

export interface ConvScopedStore<T extends object> {
  /** State de la conv courante (computed) — lecture seule du point de vue Vue. */
  readonly currentState: ComputedRef<T>
  /** Déclare quelle conv est courante. Passez `null` pour ne rien exposer. */
  setCurrentConversation: (id: string | null) => void
  /** Récupère (ou crée) le state mutable pour une conv donnée. */
  getStateForConv: (convId: string) => T
  /** Reset le state d'une conv spécifique. */
  resetStateForConv: (convId: string) => void
  /** Reset complet — toutes les convs et le pointeur courant. */
  resetAll: () => void
}

/**
 * Crée un store scopé par conversation.
 *
 * @param emptyFactory factory qui produit un état initial pour une conv donnée.
 *   L'objet retourné DOIT être un nouvel objet à chaque appel — il sera
 *   `reactive()`-isé par le store.
 */
export function createConvScopedStore<T extends object>(
  emptyFactory: (convId: string) => T,
): ConvScopedStore<T> {
  // Sentinelle frozen retournée quand aucune conv n'est sélectionnée.
  // Frozen pour éviter qu'un consommateur la mute par erreur.
  const EMPTY = Object.freeze(emptyFactory('')) as T
  const statesByConv = new Map<string, T>()
  const currentConvId = ref<string | null>(null)

  function ensureState(convId: string): T {
    const existing = statesByConv.get(convId)
    if (existing) return existing
    const fresh = reactive(emptyFactory(convId)) as T
    statesByConv.set(convId, fresh)
    return fresh
  }

  const currentState: ComputedRef<T> = computed(() => {
    const id = currentConvId.value
    return id ? ensureState(id) : EMPTY
  })

  return {
    currentState,
    setCurrentConversation(id) {
      currentConvId.value = id
    },
    getStateForConv(convId) {
      return ensureState(convId)
    },
    resetStateForConv(convId) {
      statesByConv.delete(convId)
    },
    resetAll() {
      statesByConv.clear()
      currentConvId.value = null
    },
  }
}
